import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  ConfirmationModal,
  EmptyStateBase,
  Page,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  LinkTo,
  Table,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";

import { CalendarHolidayEditModal } from "@/pages/settings/calendar-holidays/widgets/CalendarHolidayEditModal.tsx";
import {
  useCalendarHolidayDelete,
  useCalendarHolidays,
} from "@/queries/use-calendar-holidays.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiCalendarHoliday, calendarHolidayMock } from "@/types/calendar-holiday.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEffectOnce } from "react-use";

export const CalendarHolidays = () => {
  const { data: calendarHolidays, isLoading, isFetching } = useCalendarHolidays();
  const { t } = useTranslation();
  const { openModal } = useModals();
  const navigate = useNavigate();
  useTitle(t("core:calendar_holidays_title"), t("common:settings"));

  const handleEditOrCreate = useCallback((policy?: ApiCalendarHoliday) => {
    const calendarToEdit: ApiCalendarHoliday = policy || calendarHolidayMock;
    openModal(CalendarHolidayEditModal, {
      calendarHoliday: calendarToEdit,
    });
  }, []);

  const calendarHolidayDelete = useCalendarHolidayDelete();

  const handleDeleteAction = useCallback((calendarHoliday: ApiCalendarHoliday) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await calendarHolidayDelete.mutateAsync(calendarHoliday.id, {
            onSuccess: () => {
              analyticsService.trackEvent(analyticEvents.calendarHolidays.deleted, {
                [analyticProperties.id]: calendarHoliday.id,
              });
            },
          });
        } catch {}
      },
    });
  }, []);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.calendarHolidays.viewed);
  });

  const columns = useMemo<ColumnDef<ApiCalendarHoliday>[]>(
    () => [
      {
        header: t("common:title"),
        cell: (row) => (
          <LinkTo to={`/settings/holidays/${row.row.original.id}`} className={"text-title"}>
            {row.row.original.title}
          </LinkTo>
        ),
        accessorKey: "title",
        ...ColumnSizes.employee,
        meta: { disableRowClick: true },
      },
      {
        header: t("core:country"),
        cell: (row) => (
          <div className={""}>
            {row.row.original.country ? row.row.original.country.title : "-"}
          </div>
        ),
        accessorKey: "country",
        ...ColumnSizes.standard,
      },
      {
        header: t("common:location"),
        cell: (row) => (
          <div className={""}>
            {row.row.original.locations.map((location) => (
              <span key={`date-${location.id}`} className={"block"}>
                {location.title}
              </span>
            ))}
          </div>
        ),
        accessorKey: "location",
        ...ColumnSizes.status,
      },
      {
        header: t("core:departments"),
        cell: (row) => (
          <div className={""}>
            {row.row.original.departments.map((department) => (
              <span key={`date-${department.id}`} className={"block"}>
                {department.title}
              </span>
            ))}
          </div>
        ),
        accessorKey: "department",
        ...ColumnSizes.status,
      },
      {
        header: t("core:divisions"),
        cell: (row) => (
          <div className={""}>
            {row.row.original.divisions.map((division) => (
              <span key={`date-${division.id}`} className={"block"}>
                {division.title}
              </span>
            ))}
          </div>
        ),
        accessorKey: "division",
        ...ColumnSizes.status,
      },
      {
        header: t("core:employees"),
        cell: (row) => <span>{row.row.original.employees_count}</span>,
        accessorKey: "employees_count",
        ...ColumnSizes.status,
      },
    ],
    [calendarHolidays]
  );

  const renderRowAction = useCallback(
    (row: Row<ApiCalendarHoliday>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            handleEditOrCreate(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
        />
        <DropdownMenuItem
          onClick={() => {
            handleDeleteAction(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          title={t("common:delete") + "..."}
        />
      </DropdownMenu>
    ),
    [handleDeleteAction, handleEditOrCreate, t]
  );

  return (
    <Page>
      <Page.Header
        title={t("core:calendar_holidays_title")}
        showBack={true}
        navigateTo={"/settings/policies"}
      >
        <Button onClick={() => handleEditOrCreate()}>{t("common:add")}</Button>
      </Page.Header>
      <Page.Content>
        <Table
          renderRowAction={renderRowAction}
          onRowClick={(row) => navigate(`/settings/holidays/${row.original.id}`)}
          data={calendarHolidays || []}
          columns={columns}
          isFetching={isFetching || calendarHolidayDelete.isPending}
          showSkeleton={isLoading}
          notFoundComponent={<EmptyStateNothingFound />}
          emptyStateComponent={
            <EmptyStateBase
              emoji={":detective:"} //🕵️‍♀️
              title={t("core:calendar_holidays_empty_state_title")}
              description={t("core:calendar_holidays_empty_state_description")}
              primaryButton={
                <Button
                  onClick={() => {
                    handleEditOrCreate();
                  }}
                  size={"sm"}
                  variant={"secondary"}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
                >
                  {t("common:create_new")}
                </Button>
              }
            />
          }
        />
      </Page.Content>
    </Page>
  );
};
